import isEmpty from "lodash/isEmpty";
import upperFirst from "lodash/upperFirst";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { fetchProfileWarnings } from "PFApp/booking/services/api";
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import {
  useShortlistFill,
  useShortlistFillAndBook,
  useShortlistReject
} from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { ShortlistMinimized, Template } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "./assignment_actions.module.scss";
import { useShowAsInternalExperience } from "./use_show_as_internal_experience";

type AssignmentActionsProps = {
  shortlistItem: ShortlistMinimized;
  template: Template;
};

const AssignmentStatus = ({ isAccepted }: { isAccepted: boolean }) => {
  const { t } = useTranslation("translation");
  return (
    <Inline center gap="spacingSm">
      <Icon name={isAccepted ? "check" : "cross"} color="paletteBlue2" size="sm" />
      <Typography variant="labelRegular" className={css.status}>
        {isAccepted ? t("accepted") : t("declined")}
      </Typography>
    </Inline>
  );
};

export const AssignmentActions = ({ shortlistItem, template }: AssignmentActionsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["activities", "translation"]);
  const { formatISODate } = useDateFormatter();
  const { getSingleErrorByType } = useResponseErrors();
  const { showGrowl } = useGrowlStoreActions();

  const activity = useCurrentActivity();
  const { isEnabled: isBMEnabled, isPermittedToAccess: isPermittedToAccessBM } =
    useBookingModulePermissions();
  const showAsInternalExperience = useShowAsInternalExperience(activity);

  const { mutateAsync: fillShortlist } = useShortlistFill();
  const { mutateAsync: fillAndBookShortlist } = useShortlistFillAndBook();
  const { mutate: rejectShortlist } = useShortlistReject();
  const { invalidateVacancies, invalidate: invalidateActivity } = useActivityInvalidate();

  const isAutoBookingCreationEnabled = !!template?.automations?.auto_create_bookings?.enabled;
  const { state } = shortlistItem;
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const isAccepted = state === "filled";
  const isDeclined = state === "rejected";

  const errorMessage = ({ response }) => {
    const errors = getSingleErrorByType(response, {});
    return errors.id ? upperFirst(errors.id) : t("translation:somethingWentWrong");
  };
  const handleAccept = async () => {
    const options = {
      onSuccess: () => {
        activity && invalidateVacancies(activity.id);
        invalidateActivity([activity.id]);
        if (showAsInternalExperience) {
          showGrowl({
            message: t("activities:show.parts.assignmentAccepted"),
            kind: "success",
            url: "/profile/edit/internal_experience"
          });
        } else {
          showGrowl({
            message: t("activities:show.parts.assignmentSubmitted"),
            kind: "success"
          });
        }
      },
      onError: (response) => showGrowl({ message: errorMessage(response), kind: "error" })
    };

    try {
      await (isAutoBookingCreationEnabled
        ? fillAndBookShortlist(
            { shortlistId: shortlistItem.id, task: activity, profileId: shortlistItem.profileId },
            options
          )
        : fillShortlist({ id: shortlistItem.id }, options));
    } catch {
      return;
    }

    if (!isBMEnabled && isPermittedToAccessBM) {
      return;
    }

    const warningsResponse = await fetchProfileWarnings({
      profileId: shortlistItem.profileId,
      dateRange: {
        min: formatISODate(),
        max: formatISODate(UNDETERMINED_FUTURE)
      },
      newBooking: true
    });
    const overbookings = warningsResponse?.overbookings || [];
    const params = new URLSearchParams({
      workforceMemberId: String(shortlistItem.profileId),
      overbookings: !isEmpty(overbookings) ? "true" : ""
    });
    navigate({ pathname: "/booking/overview", search: params.toString() });
  };

  const handleDecline = () => {
    rejectShortlist(shortlistItem.id, {
      onSuccess: () => {
        showGrowl({ message: t("activities:show.parts.assignmentSubmitted"), kind: "success" });
      },
      onError: (response) => showGrowl({ message: errorMessage(response), kind: "error" })
    });
  };

  const message = isAccepted
    ? t("activities:show.parts.youAcceptedThisRole")
    : isDeclined
      ? t("activities:show.parts.youDeclinedThisRole")
      : t("activities:show.parts.youAreInvitedToThisRole");

  return (
    <>
      <Alert
        kind="general"
        title={
          <Inline spread className={css.alertContent}>
            <Inline center>
              <Icon name="info" />
              <Typography variant="bodyRegular">{message}</Typography>
            </Inline>
            <Inline gap={isAccepted || isDeclined ? "spacingLg" : "spacingMd"}>
              {isAccepted ? (
                <AssignmentStatus isAccepted />
              ) : (
                <Button
                  icon="check"
                  text={t("translation:accept")}
                  kind="primary"
                  qaId="role-accept"
                  onClick={handleAccept}
                />
              )}
              {isDeclined ? (
                <AssignmentStatus isAccepted={false} />
              ) : (
                <Button
                  icon="cross"
                  text={t("translation:decline")}
                  kind="ghost"
                  qaId="role-decline"
                  onClick={() => setShowDeclineConfirm(true)}
                />
              )}
            </Inline>
          </Inline>
        }
        hideIcon
      />
      {showDeclineConfirm && (
        <Modal
          title={t("activities:show.parts.declineRole")}
          labelOK={t("translation:confirm")}
          onOK={handleDecline}
          onClose={() => setShowDeclineConfirm(false)}
          kindOK="danger"
        >
          {t("activities:show.parts.assignmentConfirmDecline")}
        </Modal>
      )}
    </>
  );
};
